import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Zap,
  Target,
  BarChart3,
  Shield,
  Clock,
  Users,
  Award,
  TrendingUp,
  BookOpen,
  Filter,
  Play,
  Star,
  CheckCircle,
  ChevronRight,
  Sparkles,
  LineChart,
  Medal,
  Calculator,
  Video,
} from "lucide-react";

const Section3 = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: 0,
      title: "AI-Powered Analytics",
      description:
        "Advanced machine learning algorithms analyze your performance patterns and predict scores",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      benefits: [
        "Personalized difficulty adjustment",
        "Predictive score analysis",
        "Weakness identification",
        "Performance trend forecasting",
      ],
      stats: { value: "94%", label: "Accuracy in score prediction" },
      image: "📊",
    },
    {
      id: 1,
      title: "Real Exam Simulation",
      description:
        "Experience the actual exam environment with time-bound tests and adaptive difficulty",
      icon: Target,
      color: "from-blue-500 to-cyan-500",
      benefits: [
        "Exact exam interface",
        "Adaptive question difficulty",
        "Real-time proctoring",
        "Sectional time limits",
      ],
      stats: { value: "10K+", label: "Real exam patterns" },
      image: "🎯",
    },
    {
      id: 2,
      title: "All India Ranking",
      description:
        "Compete with aspirants nationwide and track your position with live ranking updates",
      icon: TrendingUp,
      color: "from-orange-500 to-red-500",
      benefits: [
        "Live rank updates",
        "Category-wise ranking",
        "Percentile comparison",
        "Year-over-year trends",
      ],
      stats: { value: "500K+", label: "Active competitors" },
      image: "🏆",
    },
    {
      id: 3,
      title: "Smart Performance Reports",
      description:
        "Detailed insights with actionable recommendations to improve your scores",
      icon: BarChart3,
      color: "from-green-500 to-emerald-500",
      benefits: [
        "Visual performance graphs",
        "Time management analysis",
        "Topic-wise breakdown",
        "Improvement roadmap",
      ],
      stats: { value: "35%", label: "Avg. score improvement" },
      image: "📈",
    },
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      exam: "SSC CGL 2023",
      achievement: "AIR 24",
      content:
        "The AI analytics pinpointed my weak areas perfectly. Improved my score by 45 marks in 3 months!",
      rating: 5,
      avatar: "👩‍💼",
    },
    {
      name: "Rajesh Kumar",
      exam: "Bank PO",
      achievement: "Selected in SBI",
      content:
        "Real exam simulation helped me manage exam pressure. The interface is exactly like the actual test.",
      rating: 5,
      avatar: "👨‍💼",
    },
    {
      name: "Anjali Patel",
      exam: "CAT 2023",
      achievement: "99.2 Percentile",
      content:
        "All India Ranking pushed me to perform better. Competing with top minds was motivating!",
      rating: 5,
      avatar: "👩‍🎓",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 lg:py-28">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute right-0 top-1/4 h-96 w-96 rounded-full bg-gradient-to-r from-blue-500/5 to-purple-500/5 blur-3xl" />
        <div className="absolute bottom-1/4 left-0 h-96 w-96 rounded-full bg-gradient-to-r from-cyan-500/5 to-green-500/5 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(120,119,198,0.05),transparent_50%),radial-gradient(circle_at_70%_20%,rgba(120,119,198,0.05),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2"
          >
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">
              WHY TESTSETU IS BETTER
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-6 text-4xl font-bold text-gray-900 sm:text-5xl"
          >
            Features That Make{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              All The Difference
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto max-w-3xl text-xl text-gray-600"
          >
            Discover how our intelligent platform transforms your preparation
            with cutting-edge technology
          </motion.p>
        </div>

        {/* Feature Selection Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            {features.map((feature, index) => (
              <motion.button
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveFeature(index)}
                className={`group flex items-center gap-3 rounded-xl px-5 py-4 transition-all duration-300 ${
                  activeFeature === index
                    ? "border border-gray-200 bg-white shadow-lg"
                    : "hover:bg-white/50 hover:shadow-md"
                }`}
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r ${feature.color} ${
                    activeFeature === index
                      ? "ring-4 ring-opacity-30"
                      : "group-hover:scale-110"
                  } transition-transform duration-300`}
                >
                  <feature.icon className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <h3
                    className={`text-sm font-bold ${
                      activeFeature === index
                        ? "text-gray-900"
                        : "text-gray-700 group-hover:text-gray-900"
                    }`}
                  >
                    {feature.title}
                  </h3>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Main Feature Showcase */}
        <div className="mb-20 grid items-start gap-12 lg:grid-cols-2">
          {/* Left: Feature Details */}
          <motion.div
            key={activeFeature}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <div className="mb-6 flex items-center gap-4">
                <div
                  className={`flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-r ${features[activeFeature].color}`}
                ></div>
                <div>
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    {features[activeFeature].title}
                  </h3>
                  <p className="text-gray-600">
                    {features[activeFeature].description}
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="mb-8">
                <h4 className="mb-4 font-semibold text-gray-900">
                  Key Benefits
                </h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {features[activeFeature].benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 rounded-lg border border-gray-200/50 bg-gradient-to-br from-gray-50 to-white p-4"
                    >
                      <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {benefit}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Stats Card */}
              <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">
                      {features[activeFeature].stats.value}
                    </div>
                    <div className="text-gray-600">
                      {features[activeFeature].stats.label}
                    </div>
                  </div>
                  <div className="text-5xl">
                    {features[activeFeature].image}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Interactive Demo */}
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-white to-gray-50/50 shadow-2xl">
              {/* Demo Header */}
              <div className="border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Live Demo</h4>
                      <p className="text-sm text-gray-500">
                        Interactive preview
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <div className="h-2 w-2 rounded-full bg-purple-500" />
                    <div className="h-2 w-2 rounded-full bg-cyan-500" />
                  </div>
                </div>
              </div>

              {/* Demo Content */}
              <div className="p-6">
                {/* Stats Visualization */}
                <div className="mb-8">
                  <div className="mb-4 flex items-center justify-between">
                    <h5 className="font-semibold text-gray-900">
                      Performance Analytics
                    </h5>
                    <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                      <TrendingUp className="h-4 w-4" />
                      Improving
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="mb-2 flex justify-between text-sm">
                        <span className="text-gray-600">Accuracy</span>
                        <span className="font-semibold">78% → 94%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                          style={{ width: "94%" }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 flex justify-between text-sm">
                        <span className="text-gray-600">Speed</span>
                        <span className="font-semibold">65% → 88%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                          style={{ width: "88%" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Rank Card */}
                <div className="mb-6 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Medal className="h-6 w-6 text-yellow-500" />
                      <div>
                        <div className="font-semibold text-gray-900">
                          Your Current Rank
                        </div>
                        <div className="text-sm text-gray-600">
                          Updated just now
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        #1,234
                      </div>
                      <div className="text-sm text-green-600">
                        ↑ 128 positions
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 py-3 font-semibold text-white hover:shadow-lg">
                    <Play className="h-4 w-4" />
                    Try Demo
                  </button>
                  <button className="flex items-center justify-center gap-2 rounded-lg border border-blue-600 bg-white py-3 font-semibold text-blue-600 hover:bg-blue-50">
                    <Video className="h-4 w-4" />
                    Watch Video
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Success Card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="absolute -right-4 -top-4 z-10 w-56 rounded-2xl border border-gray-200 bg-white p-5 shadow-xl"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500">
                  <Calculator className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Score Predictor</div>
                  <div className="mt-1 text-sm text-gray-600">94% accuracy</div>
                  <div className="mt-2 text-xs text-gray-500">
                    Based on 10M+ data points
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mb-16">
          <div className="mb-12 text-center">
            <h3 className="mb-4 text-3xl font-bold text-gray-900">
              Success Stories That{" "}
              <span className="text-blue-600">Inspire</span>
            </h3>
            <p className="mx-auto max-w-2xl text-gray-600">
              Hear from our top performers who transformed their preparation
              with TestSetu
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 p-6 transition-all duration-300 hover:border-blue-300 hover:shadow-xl"
              >
                {/* Rating */}
                <div className="mb-4 flex items-center gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                {/* Content */}
                <p className="mb-6 italic text-gray-700">
                  "{testimonial.content}"
                </p>

                {/* User Info */}
                <div className="flex items-center gap-4">
                  <div className="text-3xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {testimonial.exam}
                      </span>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {testimonial.achievement}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50/50">
          <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white p-6">
            <h3 className="text-2xl font-bold text-gray-900">
              Why Choose TestSetu Over Others?
            </h3>
            <p className="mt-2 text-gray-600">Feature-by-feature comparison</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="p-6 text-left font-semibold text-gray-900">
                    Feature
                  </th>
                  <th className="p-6 text-center font-semibold text-blue-600">
                    TestSetu
                  </th>
                  <th className="p-6 text-center font-semibold text-gray-600">
                    Other Platforms
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    feature: "AI-Powered Analytics",
                    testsetu: "✓ Advanced",
                    others: "Basic",
                  },
                  {
                    feature: "Real Exam Simulation",
                    testsetu: "✓ Complete",
                    others: "Partial",
                  },
                  {
                    feature: "Live All India Ranking",
                    testsetu: "✓ Real-time",
                    others: "Weekly",
                  },
                  {
                    feature: "Personalized Roadmaps",
                    testsetu: "✓ Adaptive",
                    others: "Static",
                  },
                  {
                    feature: "24/7 Expert Support",
                    testsetu: "✓ Available",
                    others: "Limited",
                  },
                ].map((row, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-200 last:border-0"
                  >
                    <td className="p-6 font-medium text-gray-900">
                      {row.feature}
                    </td>
                    <td className="p-6 text-center">
                      <span className="inline-flex items-center gap-2 font-semibold text-green-600">
                        <CheckCircle className="h-5 w-5" />
                        {row.testsetu}
                      </span>
                    </td>
                    <td className="p-6 text-center text-gray-600">
                      {row.others}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-200 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 p-6">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div>
                <h4 className="font-bold text-gray-900">
                  Ready to Experience the Difference?
                </h4>
                <p className="text-gray-600">Start your free trial today</p>
              </div>
              <button className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-semibold text-white hover:shadow-lg">
                Start 7-Day Free Trial
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Section3;
